import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, ArrowUpRight, LogOut, Loader2, MessageSquare } from "lucide-react";
import { APP } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { useIdeas, useSavedIdeas } from "@/hooks/useIdeas";
import { useDeepDive } from "@/hooks/useDeepDive";
import IdeaCard from "@/components/IdeaCard";
import SavedIdeaCard from "@/components/SavedIdeaCard";
import IdeaFilters from "@/components/IdeaFilters";
import LiveSources from "@/components/LiveSources";
import DeepDiveDrawer from "@/components/DeepDiveDrawer";
import FeedbackDialog from "@/components/FeedbackDialog";

const buildInitialFilters = (user) => ({
  category: "Any",
  budget: "Any",
  skill: "Any",
  market: "Any",
  country: user?.country || "India",
  currency: user?.currency || "INR",
  count: 6,
});

function TopNav({ user, onLogout, onOpenFeedback }) {
  return (
    <nav className="sticky top-0 z-30 backdrop-blur-xl bg-[var(--bg)]/85 border-b border-[color:var(--fg)]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" data-testid={APP.navLogo} className="flex items-center gap-2">
          <div className="w-7 h-7 bg-[color:var(--fg)] text-[color:var(--bg)] flex items-center justify-center font-display text-sm">T</div>
          <span className="font-display text-lg hidden sm:inline">TRENDING/IDEAS</span>
        </Link>
        <div className="flex items-center gap-3">
          <button
            data-testid={APP.feedbackNavBtn}
            onClick={onOpenFeedback}
            className="px-3 py-2 border border-[color:var(--fg)] text-xs uppercase tracking-widest font-semibold hover:bg-[color:var(--yellow)] transition-colors flex items-center gap-2"
          >
            <MessageSquare size={14} /><span className="hidden sm:inline">Feedback</span>
          </button>
          <div className="hidden md:block text-right">
            <div className="text-xs font-mono-tight text-[color:var(--muted)]">// operator</div>
            <div className="text-sm font-semibold">{user.name || user.email}</div>
          </div>
          <button
            data-testid={APP.navLogout}
            onClick={onLogout}
            className="px-3 py-2 border border-[color:var(--fg)] text-xs uppercase tracking-widest font-semibold hover:bg-[color:var(--fg)] hover:text-[color:var(--bg)] transition-colors flex items-center gap-2"
          >
            <LogOut size={14} /><span className="hidden sm:inline">Log out</span>
          </button>
        </div>
      </div>
    </nav>
  );
}

function Tabs({ tab, setTab, savedCount }) {
  return (
    <div className="flex gap-0 border-b border-[color:var(--fg)] mb-8">
      <button
        data-testid={APP.tabGenerator}
        onClick={() => setTab("generator")}
        className={`px-6 py-3 font-display text-xl uppercase tracking-tight transition-colors ${
          tab === "generator" ? "bg-[color:var(--fg)] text-[color:var(--bg)]" : "hover:bg-[color:var(--fg)] hover:text-[color:var(--bg)]"
        }`}
      >
        Generator
      </button>
      <button
        data-testid={APP.tabSaved}
        onClick={() => setTab("saved")}
        className={`px-6 py-3 font-display text-xl uppercase tracking-tight transition-colors flex items-center gap-2 ${
          tab === "saved" ? "bg-[color:var(--fg)] text-[color:var(--bg)]" : "hover:bg-[color:var(--fg)] hover:text-[color:var(--bg)]"
        }`}
      >
        Saved
        <span className="text-xs font-mono-tight">({savedCount})</span>
      </button>
    </div>
  );
}

function GeneratorView({ filters, setFilters, ideas, sources, expanded, loading, error, savedMap, onGenerate, onSave, onToggle, onDeepDive }) {
  return (
    <>
      <div className="brutal-border p-5 sm:p-6 mb-8">
        <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
          <div className="min-w-0">
            <div className="tag">// filters</div>
            <h2 className="font-display text-2xl sm:text-3xl mt-2">Configure your search</h2>
          </div>
          <button
            data-testid={APP.generateBtn}
            onClick={onGenerate}
            disabled={loading}
            className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-[color:var(--fg)] text-[color:var(--bg)] font-semibold uppercase tracking-wider text-sm disabled:opacity-60 flex items-center justify-center gap-3 brutal-shadow"
          >
            {loading ? (
              <><Loader2 size={16} className="animate-spin" /> Generating...</>
            ) : (
              <><Sparkles size={16} /> Generate ideas <ArrowUpRight size={16} /></>
            )}
          </button>
        </div>
        <IdeaFilters filters={filters} setFilters={setFilters} />
      </div>

      {loading && (
        <div data-testid={APP.generateLoading} className="brutal-border p-16 text-center">
          <Loader2 size={32} className="animate-spin mx-auto text-[color:var(--muted)]" />
          <div className="mt-4 font-display text-2xl">Scanning market signals...</div>
          <div className="mt-2 text-sm text-[color:var(--muted)] font-mono-tight">// this takes ~10-20s</div>
        </div>
      )}

      {error && !loading && (
        <div data-testid={APP.generateError} className="brutal-border border-[color:var(--hot)] p-6 mb-6">
          <div className="text-[color:var(--hot)] font-semibold">Error</div>
          <div className="text-sm mt-1">{error}</div>
        </div>
      )}

      {ideas.length > 0 && (
        <>
          <LiveSources sources={sources} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ideas.map((idea) => (
              <IdeaCard
                key={idea.id}
                idea={idea}
                isSaved={!!savedMap[idea.id]}
                onSave={onSave}
                onDeepDive={onDeepDive}
                expanded={!!expanded[idea.id]}
                onToggle={() => onToggle(idea.id)}
              />
            ))}
          </div>
        </>
      )}

      {!loading && !error && ideas.length === 0 && (
        <div className="text-center py-24 border border-dashed border-[color:var(--line)]">
          <div className="font-display text-4xl">Nothing yet.</div>
          <div className="text-sm text-[color:var(--muted)] mt-2">Set your filters and hit generate.</div>
        </div>
      )}
    </>
  );
}

function SavedView({ saved, onDelete, onDeepDive, goToGenerator }) {
  return (
    <div data-testid={APP.savedList}>
      {saved.length === 0 ? (
        <div data-testid={APP.savedEmpty} className="text-center py-24 border border-dashed border-[color:var(--line)]">
          <div className="font-display text-4xl">No saved ideas.</div>
          <div className="text-sm text-[color:var(--muted)] mt-2">Generate some and bookmark the ones worth building.</div>
          <button
            onClick={goToGenerator}
            className="mt-6 px-6 py-3 bg-[color:var(--fg)] text-[color:var(--bg)] uppercase tracking-widest text-xs font-semibold"
          >
            Go to generator →
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {saved.map((s) => (
            <SavedIdeaCard key={s.id} saved={s} onDelete={onDelete} onDeepDive={onDeepDive} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user, logout, bootstrapping } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("generator");
  const [filters, setFilters] = useState(() => buildInitialFilters(user));
  const [diveIdea, setDiveIdea] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const { ideas, sources, expanded, loading, error, generate, toggleExpand } = useIdeas();
  const { saved, load, saveIdea, deleteSaved } = useSavedIdeas();
  const {
    dive,
    loading: diveLoading,
    error: diveError,
    regenerating: regeneratingReality,
    fetchDive,
    regenerateReality,
    shareCurrent,
    reset: resetDive,
  } = useDeepDive();

  const savedMap = useMemo(() => {
    const map = {};
    saved.forEach((s) => { map[s.idea.id] = true; });
    return map;
  }, [saved]);

  useEffect(() => {
    if (user) {
      setFilters((f) => ({
        ...f,
        country: f.country || user.country || "India",
        currency: f.currency || user.currency || "INR",
      }));
    }
  }, [user]);

  useEffect(() => {
    if (bootstrapping) return;
    if (!user) {
      nav("/auth?mode=login", { replace: true });
      return;
    }
    load();
  }, [user, bootstrapping, nav, load]);

  const doLogout = async () => {
    nav("/", { replace: true });
    await logout();
  };

  const handleGenerate = async () => {
    await generate(filters);
  };

  const openDeepDive = (idea) => {
    setDiveIdea(idea);
    fetchDive(idea);
  };

  const closeDeepDive = (open) => {
    if (!open) {
      setDiveIdea(null);
      resetDive();
    }
  };

  if (bootstrapping || !user) return null;

  return (
    <div className="min-h-screen grain">
      <TopNav user={user} onLogout={doLogout} onOpenFeedback={() => setFeedbackOpen(true)} />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <Tabs tab={tab} setTab={setTab} savedCount={saved.length} />
        {tab === "generator" ? (
          <GeneratorView
            filters={filters}
            setFilters={setFilters}
            ideas={ideas}
            sources={sources}
            expanded={expanded}
            loading={loading}
            error={error}
            savedMap={savedMap}
            onGenerate={handleGenerate}
            onSave={saveIdea}
            onToggle={toggleExpand}
            onDeepDive={openDeepDive}
          />
        ) : (
          <SavedView
            saved={saved}
            onDelete={deleteSaved}
            onDeepDive={openDeepDive}
            goToGenerator={() => setTab("generator")}
          />
        )}
      </div>
      <DeepDiveDrawer
        open={!!diveIdea}
        onOpenChange={closeDeepDive}
        idea={diveIdea}
        dive={dive}
        loading={diveLoading}
        error={diveError}
        onRegenerateReality={regenerateReality}
        regenerating={regeneratingReality}
        onShare={shareCurrent}
      />
      <FeedbackDialog
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        defaultEmail={user?.email}
      />
    </div>
  );
}
